import React, { useContext, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getAvailableModels, DEFAULT_MODELS } from '../llm/llm_constants';
import SettingsContext from '../contexts/SettingsContext';
import { useModal } from '../contexts/ModalContext';
import ModalShell from './ModalShell';
import { hasTier } from '../game/entitlements';
import AiPoolPills from './AiPoolPills';
import {
  getPreferredPool, setPreferredPool,
  getLastPoolOutcome, subscribeAiPool
} from '../services/aiPool';

// --- Share QR Code (inline expandable) --- //
export const ShareQRCode = () => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = window.location.origin;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ textAlign: 'center', marginTop: '8px' }}>
      <button
        onClick={() => setShow(!show)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: '0.75rem',
          fontFamily: 'var(--header-font)', letterSpacing: '0.5px',
          opacity: 0.6, transition: 'opacity 0.2s'
        }}
        onMouseEnter={e => e.target.style.opacity = '1'}
        onMouseLeave={e => e.target.style.opacity = '0.6'}
      >
        {show ? '▾ Hide QR Code' : '▸ Share this game'}
      </button>
      {show && (
        <div style={{
          marginTop: '12px', padding: '16px',
          background: '#ffffff', borderRadius: '12px',
          display: 'inline-block', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <QRCodeSVG value={url} size={160} level="M" />
          <div style={{
            marginTop: '8px', fontSize: '0.7rem',
            color: '#666', wordBreak: 'break-all', maxWidth: '160px'
          }}>
            {url}
          </div>
          <button
            onClick={handleCopyLink}
            style={{
              marginTop: '10px', padding: '6px 16px',
              background: copied ? '#4a7c59' : '#555',
              color: '#fff', border: 'none', borderRadius: '6px',
              cursor: 'pointer', fontSize: '0.7rem',
              fontFamily: 'var(--header-font)', letterSpacing: '0.5px',
              transition: 'background 0.2s'
            }}
          >
            {copied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
      )}
    </div>
  );
};

// --- AI engine settings (provider/model pickers) --- //
// Shared by the navbar AI Settings modal and the Adventure Book's ⚙️ AI tab (#52),
// so the two surfaces cannot drift.
// Production hides the provider CHOICE entirely: only cf-workers is wired in prod
// (other providers route to the local dev Express server), and a pool-based model
// system (#7 premium AI) will replace provider picking. Dev/debug builds keep the
// full picker for local SDK/CLI testing.
const SHOW_PROVIDER_CHOICE =
  process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_DEBUG_ROUTES === 'true';

export const AiEngineSettings = ({
  selectedProvider, setSelectedProvider,
  selectedModel, setSelectedModel,
  assistantProvider, setAssistantProvider,
  assistantModel, setAssistantModel
}) => {
  const AVAILABLE_MODELS = getAvailableModels();

  // AI pool state (backlog #7): preference lives in src/services/aiPool.js (module
  // state + localStorage, same pattern as entitlements). The chip only goes live
  // for member+ accounts; llmService gates the actual request the same way, so a
  // lapsed tier silently reverts to the free pool without losing the preference.
  const premiumUnlocked = hasTier('member');
  const [pool, setPool] = useState(getPreferredPool());
  const [poolOutcome, setPoolOutcome] = useState(getLastPoolOutcome());
  useEffect(() => subscribeAiPool(() => {
    setPool(getPreferredPool());
    setPoolOutcome(getLastPoolOutcome());
  }), []);
  const premiumActive = premiumUnlocked && pool === 'premium';
  // The premium pool's model is chosen SERVER-SIDE (openrouter.ts default chain);
  // the client never selects it, so any model dropdown selection is ignored while
  // Premium is active. This label is informational only (kept vague on purpose:
  // "the pool is the choice", not the model).
  const PREMIUM_MODEL_LABEL = 'OpenRouter (default openai/gpt-5-mini; the server may fall back down the premium chain, then to the Free pool)';

  // Production coercion: any stored/legacy non-cf-workers provider is pinned back to
  // the one provider that actually works in prod (prevents silent AI breakage for
  // players who picked a dev provider before this gate existed).
  useEffect(() => {
    if (SHOW_PROVIDER_CHOICE || !AVAILABLE_MODELS['cf-workers']) return;
    if (selectedProvider !== 'cf-workers') {
      setSelectedProvider('cf-workers');
      setSelectedModel(DEFAULT_MODELS['cf-workers']);
    }
    if (assistantProvider && assistantProvider !== 'cf-workers') {
      setAssistantProvider('cf-workers');
      setAssistantModel(DEFAULT_MODELS['cf-workers']);
    }
  }, [selectedProvider, assistantProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProviderChange = (newProvider, type) => {
    if (type === 'game') {
      setSelectedProvider(newProvider);
      setSelectedModel(DEFAULT_MODELS[newProvider]);
    } else {
      setAssistantProvider(newProvider);
      setAssistantModel(DEFAULT_MODELS[newProvider]);
    }
  };

  const renderPicker = (title, type, provider, model, setModel) => (
    <div style={{ marginBottom: '20px', background: 'var(--bg)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)' }}>
      <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: 'var(--primary)', fontFamily: 'var(--header-font)' }}>{title}</h4>
      <div style={{ display: 'grid', gridTemplateColumns: SHOW_PROVIDER_CHOICE ? '1fr 1fr' : '1fr', gap: '10px' }}>
        {SHOW_PROVIDER_CHOICE && <div>
          <label style={labelStyle}>PROVIDER</label>
          <select value={provider} onChange={(e) => handleProviderChange(e.target.value, type)} style={selectStyle}>
            {Object.keys(AVAILABLE_MODELS).includes('openai') && (
              <optgroup label="Cloud APIs">
                {AVAILABLE_MODELS['openai'] && <option value="openai">OpenAI</option>}
                {AVAILABLE_MODELS['gemini'] && <option value="gemini">Gemini</option>}
                {AVAILABLE_MODELS['claude'] && <option value="claude">Claude</option>}
              </optgroup>
            )}
            {AVAILABLE_MODELS['cf-workers'] && (
              <optgroup label="CloudFlare Workers">
                <option value="cf-workers">CF Workers AI</option>
              </optgroup>
            )}
            {(AVAILABLE_MODELS['codex'] || AVAILABLE_MODELS['claude-cli'] || AVAILABLE_MODELS['gemini-cli']) && (
              <optgroup label="CLI Tools">
                {AVAILABLE_MODELS['codex'] && <option value="codex">Codex CLI</option>}
                {AVAILABLE_MODELS['claude-cli'] && <option value="claude-cli">Claude CLI</option>}
                {AVAILABLE_MODELS['gemini-cli'] && <option value="gemini-cli">Gemini CLI</option>}
              </optgroup>
            )}
          </select>
        </div>}
        <div>
          <label style={labelStyle}>MODEL</label>
          <select value={model} onChange={(e) => setModel(e.target.value)} style={selectStyle}>
            {AVAILABLE_MODELS[provider]?.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            )) || <option value="">Select Provider</option>}
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-section">
      <h3 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: 'var(--text)' }}>🤖 AI Configuration</h3>
      {/* AI pool selector (backlog #7): Free is live for everyone; Premium (the
          Members OpenRouter pool) goes live for member+ accounts and stays locked
          for guests/free exactly as before. The Worker enforces tier + a daily
          premium allowance server-side; over-allowance responses quietly fall back
          to the free pool (notice below). */}
      <AiPoolPills style={{ marginBottom: premiumActive ? '8px' : '20px' }} />
      {/* Active-engine resolver: DEV ONLY. Locally the provider dropdown and the
          pool pills can disagree (a non-cf-workers provider routes to the Express
          dev server and bypasses the pool), so this line states what will
          actually handle the next request. Production has no dropdown to disagree
          with, so the pool pills alone are the truth and this line would be
          clutter (maintainer 2026-07-06). */}
      {SHOW_PROVIDER_CHOICE && (() => {
        const onWorker = selectedProvider === 'cf-workers';
        return (
          <div
            data-testid="active-engine"
            style={{
              marginBottom: '12px', padding: '8px 10px', borderRadius: '6px',
              fontSize: '0.78rem', lineHeight: 1.4,
              background: onWorker ? 'var(--surface)' : 'rgba(224,168,0,0.12)',
              border: `1px solid ${onWorker ? 'var(--border)' : 'var(--state-warning, #e0a800)'}`,
              color: 'var(--text-secondary)',
            }}
          >
            {onWorker ? (
              premiumActive ? (
                <span><strong style={{ color: 'var(--text)' }}>Active engine:</strong> ✨ Premium pool: {PREMIUM_MODEL_LABEL}. Requests route through the Cloudflare Worker to OpenRouter.<br />The provider/model pickers below are ignored while Premium is active.</span>
              ) : (
                <span><strong style={{ color: 'var(--text)' }}>Active engine:</strong> Cloudflare Worker, ⚡ Free pool</span>
              )
            ) : (
              <span>
                <strong style={{ color: 'var(--state-warning, #e0a800)' }}>⚠ Active engine: {selectedProvider} (local dev server)</strong>
                <br />The Free/Premium pool applies only to the Cloudflare Worker. Pick "CF Workers" as the provider below to exercise the pool.
              </span>
            )}
          </div>
        );
      })()}
      {premiumActive && (
        <div style={{ marginBottom: '20px', fontSize: '0.78rem', color: 'var(--text-secondary)' }} data-testid="pool-status">
          {poolOutcome?.reason === 'premium_cap' ? (
            <span>Today's Premium allowance is used up — responses are coming from the Free pool until it resets.</span>
          ) : poolOutcome?.reason === 'premium_error' ? (
            <span>Premium AI is briefly unavailable — responses fall back to the Free pool automatically.</span>
          ) : poolOutcome?.reason === 'premium_required' ? (
            <span>Premium AI needs an active Membership — responses are coming from the Free pool.</span>
          ) : poolOutcome ? (
            <span>Last response served by: {poolOutcome.usedPool === 'premium' ? 'Premium AI' : 'Free AI'}</span>
          ) : (
            <span>Premium AI selected — the next response will use the premium pool.</span>
          )}
        </div>
      )}
      {/* Production shows NO provider/model dropdowns (maintainer decision: too many
          choices — the pool IS the choice). The free pool runs on managed cf-workers
          models; Premium (#7) adds the OpenRouter pool later. Dev keeps the pickers. */}
      {/* Production hides the provider/model pickers entirely: the pool pills above
          are the only AI choice, so no dropdowns and no active-model readout here. */}
      {SHOW_PROVIDER_CHOICE && (
        <div style={premiumActive ? { opacity: 0.5, pointerEvents: 'none' } : undefined} aria-disabled={premiumActive}>
          {premiumActive && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '6px', fontStyle: 'italic' }}>
              Provider/model selection does not apply while the Premium pool is active (the premium model is server-selected).
            </div>
          )}
          {renderPicker('Narrative DM', 'game', selectedProvider, selectedModel, setSelectedModel)}
          {renderPicker('OOC Assistant', 'assistant', assistantProvider || selectedProvider, assistantModel || selectedModel, setAssistantModel)}
        </div>
      )}
    </div>
  );
};

// --- AI-Only Settings Modal (for Navbar/Homepage) --- //
export const AISettingsModalContent = ({
  isOpen, onClose,
  selectedProvider, setSelectedProvider,
  selectedModel, setSelectedModel,
  assistantProvider, setAssistantProvider,
  assistantModel, setAssistantModel
}) => {
  const { theme, setTheme } = useContext(SettingsContext);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settings-modal-refined" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 20px 10px 20px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ margin: 0, color: 'var(--primary)', fontFamily: 'var(--header-font)', fontSize: '1.4rem' }}>⚙️ AI Settings</h2>
        </div>

        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {/* AI Engine Settings */}
          <AiEngineSettings
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            assistantProvider={assistantProvider}
            setAssistantProvider={setAssistantProvider}
            assistantModel={assistantModel}
            setAssistantModel={setAssistantModel}
          />

          {/* Theme Selection */}
          <div className="modal-section" style={{ marginTop: '25px' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: 'var(--primary)', fontFamily: 'var(--header-font)' }}>🎭 Appearance</h3>
            <div style={{ display: 'flex', gap: '15px' }}>
              <button
                onClick={() => setTheme('light-fantasy')}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: theme === 'light-fantasy' ? 'var(--primary)' : 'var(--bg)',
                  color: theme === 'light-fantasy' ? 'var(--bg)' : 'var(--text)',
                  border: '1px solid var(--primary)',
                  fontFamily: 'var(--header-font)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: theme === 'light-fantasy' ? '0 4px 12px var(--shadow)' : 'none'
                }}
              >
                📜 Parchment (Light)
              </button>
              <button
                onClick={() => setTheme('dark-fantasy')}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: theme === 'dark-fantasy' ? 'var(--primary)' : 'var(--bg)',
                  color: theme === 'dark-fantasy' ? 'var(--bg)' : 'var(--text)',
                  border: '1px solid var(--primary)',
                  fontFamily: 'var(--header-font)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: theme === 'dark-fantasy' ? '0 4px 12px var(--shadow)' : 'none'
                }}
              >
                🌑 Stone (Dark)
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px', borderTop: '1px solid var(--border)', textAlign: 'center', background: 'var(--bg)' }}>
          <button className="modal-close-button" onClick={onClose} style={{ padding: '12px 60px', borderRadius: '30px', fontFamily: 'var(--header-font)', textTransform: 'uppercase', letterSpacing: '2px' }}>
            Accept & Close
          </button>
          <ShareQRCode />
        </div>
      </div>
    </div>
  );
};

const selectStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '6px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--surface)',
  fontSize: '0.9rem',
  color: 'var(--text)',
  boxSizing: 'border-box'
};

const labelStyle = {
  fontSize: '0.7rem',
  fontWeight: 'bold',
  color: 'var(--text-secondary)',
  display: 'block',
  marginBottom: '4px',
  letterSpacing: '0.5px'
};

// --- How to Play Modal --- //
export const HowToPlayModalContent = () => {
  const { close } = useModal('howToPlay');

  return (
    <ModalShell modalId="howToPlay" ariaLabel="How to Play" style={{ maxWidth: '500px' }}>
      <h2 style={{ fontFamily: 'var(--header-font)', color: 'var(--primary)', textAlign: 'center' }}>📜 Rules of Engagement</h2>
      <div style={{ padding: '20px 0', lineHeight: '1.8', color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <p>
          Welcome, traveler! Your journey begins with your party of heroes.
        </p>
        <p>
          ⚔️ <strong>Commanding:</strong> Type your actions in the scroll below and the AI Dungeon Master will weave the tapestry of your fate.
        </p>
        <p>
          🗺️ <strong>Exploration:</strong> Click adjacent tiles on the world map to move your party. Enter towns to visit taverns, shops, temples, and more.
        </p>
        <p>
          🎲 <strong>Encounters:</strong> Beasts and brigands lurk in the wilds. When danger strikes, choose your actions wisely from the options presented.
        </p>
        <p>
          📜 <strong>Quests:</strong> Follow your campaign milestones to advance the story. Track your progress in the quest log.
        </p>
        <p>
          🎒 <strong>Inventory:</strong> Collect loot, potions, and equipment. Open the party inventory to manage your spoils.
        </p>
        <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '15px' }}>
          Note: The sidebar tracks your party's vitality and stats. Keep a keen eye upon it.
        </p>
      </div>
      <div style={{ textAlign: 'center' }}>
        <button className="modal-close-button" onClick={close} style={{ width: '100%', padding: '12px' }}>
          Begone! I have an adventure to start.
        </button>
      </div>
    </ModalShell>
  );
};
